import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { wishlistAPI } from '../services/api';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const WishlistContext = createContext();

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};

export const WishlistProvider = ({ children }) => {
  const { user } = useAuth();
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(false);

  // Get user ID
  const getUserId = useCallback(() => {
    if (user?.id || user?._id) {
      return user.id || user._id;
    }
    let guestId = localStorage.getItem('guest_id');
    if (!guestId) {
      guestId = 'guest_' + Math.random().toString(36).substring(2, 15);
      localStorage.setItem('guest_id', guestId);
    }
    return guestId;
  }, [user]);

  // Fetch wishlist
  const fetchWishlist = useCallback(async () => {
    const userId = getUserId();
    setLoading(true);
    try {
      const response = await wishlistAPI.getProducts(userId);
      setWishlist(response.data || []);
    } catch (error) {
      console.error('Error fetching wishlist:', error);
    } finally {
      setLoading(false);
    }
  }, [getUserId]);

  useEffect(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  // Add to wishlist
  const addToWishlist = async (productId, productName = 'Product') => {
    const userId = getUserId();
    try {
      await wishlistAPI.add(userId, productId);
      await fetchWishlist();
      
      // Dispatch custom event for wishlist animation
      window.dispatchEvent(new CustomEvent('wishlistUpdated', { detail: { action: 'add' } }));
      
      toast.success('Added to Wishlist!', {
        description: productName.length > 50 ? productName.substring(0, 50) + '...' : productName,
        action: {
          label: 'View Wishlist',
          onClick: () => window.location.href = '/wishlist'
        },
        duration: 4000
      });
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      toast.error('Failed to Add', {
        description: 'Could not add to wishlist. Please try again.'
      });
    }
  };

  // Remove from wishlist
  const removeFromWishlist = async (productId) => {
    const userId = getUserId();
    try {
      await wishlistAPI.remove(userId, productId);
      await fetchWishlist();
      toast.success('Removed from Wishlist');
    } catch (error) {
      console.error('Error removing from wishlist:', error);
    }
  };

  // Check if product is in wishlist
  const isInWishlist = (productId) => {
    return wishlist.some(item => (item._id || item.id) === productId);
  };

  // Toggle wishlist
  const toggleWishlist = async (productId, productName = 'Product') => {
    if (isInWishlist(productId)) {
      await removeFromWishlist(productId);
    } else {
      await addToWishlist(productId, productName);
    }
  };

  return (
    <WishlistContext.Provider value={{
      wishlist,
      loading,
      addToWishlist,
      removeFromWishlist,
      isInWishlist,
      toggleWishlist,
      fetchWishlist,
      count: wishlist.length
    }}>
      {children}
    </WishlistContext.Provider>
  );
};
